import { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { CheckCircleIcon, FileIcon, TrashIcon, UploadIcon } from 'lucide-react'

const MAX_FILENAME_LENGTH = 20

/**
 * Truncates a filename keeping the extension visible
 * e.g. "very_long_filename_document.pdf" -> "very_long_fi...ent.pdf"
 */
const truncateFileName = (fileName: string, maxLength: number = MAX_FILENAME_LENGTH): string => {
  if (fileName.length <= maxLength) return fileName

  const lastDotIndex = fileName.lastIndexOf('.')
  const hasExtension = lastDotIndex > 0 && lastDotIndex > fileName.length - 6

  if (hasExtension) {
    const extension = fileName.slice(lastDotIndex)
    const nameWithoutExt = fileName.slice(0, lastDotIndex)
    const availableLength = maxLength - extension.length - 3 // 3 for "..."

    if (availableLength <= 4) {
      // If too short, just truncate from the end
      return fileName.slice(0, maxLength - 3) + '...'
    }

    const startLength = Math.ceil(availableLength * 0.6)
    const endLength = availableLength - startLength

    return (
      nameWithoutExt.slice(0, startLength) + '...' + nameWithoutExt.slice(-endLength) + extension
    )
  }

  // No extension, simple truncate
  return fileName.slice(0, maxLength - 3) + '...'
}

export type DocumentUploadFieldProps = {
  label: string
  accept?: string
  maxSizeMB?: number
  currentFile?: string | null
  onUpload: (_file: File) => Promise<{ path: string } | void>
  onRemove?: () => Promise<void>
  disabled?: boolean
  helperText?: string
}

const DocumentUploadField = memo(
  ({
    label,
    accept = '.pdf,.jpg,.jpeg,.png,.gif',
    maxSizeMB = 2,
    currentFile,
    onUpload,
    onRemove,
    disabled = false,
    helperText
  }: DocumentUploadFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [uploadedPath, setUploadedPath] = useState<string | null>(currentFile || null)
    const [justUploaded, setJustUploaded] = useState(false)

    // Sync uploadedPath with currentFile prop when it changes
    useEffect(() => {
      setUploadedPath(currentFile || null)
    }, [currentFile])

    // Clear justUploaded state after a delay
    useEffect(() => {
      if (justUploaded) {
        const timer = setTimeout(() => setJustUploaded(false), 3000)
        return () => clearTimeout(timer)
      }
    }, [justUploaded])

    const handleFileSelect = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        // Validate file size
        if (file.size > maxSizeMB * 1024 * 1024) {
          setError(`Il file supera il limite di ${maxSizeMB}MB`)
          return
        }

        setError(null)
        setIsUploading(true)
        setJustUploaded(false)

        try {
          const result = await onUpload(file)
          if (result?.path) {
            setUploadedPath(result.path)
            setJustUploaded(true)
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Errore durante il caricamento')
        } finally {
          setIsUploading(false)
          // Reset input
          if (inputRef.current) {
            inputRef.current.value = ''
          }
        }
      },
      [maxSizeMB, onUpload]
    )

    const handleRemove = useCallback(async () => {
      if (!onRemove) return

      setIsUploading(true)
      try {
        await onRemove()
        setUploadedPath(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore durante la rimozione')
      } finally {
        setIsUploading(false)
      }
    }, [onRemove])

    const handleClick = () => {
      inputRef.current?.click()
    }

    const fileName = uploadedPath?.split('/').pop() || null

    return (
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 500, mb: 1, display: 'block' }}
        >
          {label}
        </Typography>

        <Box
          sx={{
            border: '2px dashed',
            borderColor: error ? 'error.main' : uploadedPath ? 'success.main' : 'divider',
            borderRadius: 2,
            p: 2,
            backgroundColor: uploadedPath ? 'success.50' : 'background.paper',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: disabled ? 'divider' : 'primary.main'
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={disabled || isUploading}
          />

          {isUploading ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ py: 1 }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Caricamento in corso...
              </Typography>
            </Stack>
          ) : uploadedPath ? (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  backgroundColor: 'success.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'success.main'
                }}
              >
                <CheckCircleIcon size={20} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <FileIcon size={14} />
                  <Tooltip title={fileName ?? ''} placement="top">
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {fileName ? truncateFileName(fileName) : ''}
                    </Typography>
                  </Tooltip>
                  {justUploaded && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'success.main',
                        fontWeight: 600,
                        ml: 1
                      }}
                    >
                      ✓ Caricato
                    </Typography>
                  )}
                </Stack>
                <Link
                  href={
                    uploadedPath.startsWith('http')
                      ? uploadedPath
                      : uploadedPath.startsWith(`advertisements`)
                        ? `/doc/${uploadedPath.startsWith('/') ? uploadedPath.slice(1) : uploadedPath}`
                        : `/${uploadedPath.startsWith('/') ? uploadedPath.slice(1) : uploadedPath}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  color="primary"
                >
                  Visualizza documento
                </Link>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClick}
                  disabled={disabled}
                  startIcon={<UploadIcon size={14} />}
                >
                  Carica nuovo file
                </Button>
                {onRemove && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleRemove}
                    disabled={disabled}
                  >
                    <TrashIcon size={14} />
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack
              alignItems="center"
              spacing={1}
              sx={{ py: 1, cursor: disabled ? 'default' : 'pointer' }}
              onClick={disabled ? undefined : handleClick}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: 'primary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main'
                }}
              >
                <UploadIcon size={24} />
              </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Clicca per caricare o trascina qui il file
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Formati: {accept} - Max {maxSizeMB}MB
              </Typography>
            </Stack>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        {helperText && !error && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {helperText}
          </Typography>
        )}
      </Box>
    )
  }
)

DocumentUploadField.displayName = 'DocumentUploadField'

export default DocumentUploadField
